import { IonInfiniteScroll } from '@ionic/react'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import moment from 'moment-timezone'
import { useMemo } from 'react'

import { useGetUnitControlHistoryByControl } from '@apps-common/apis/portal/control'
import { InfiniteLoader } from '@apps-common/components/common/InfiniteLoader'
import { PullRefresh } from '@apps-common/components/common/PullRefresh'
import {
  CONTROL_STATE_OFF,
  CONTROL_STATE_ON,
  CONTROL_STATE_UNKNOWN,
  CONTROL_TYPE_ACCESS_CONTROL,
  CONTROL_TYPE_ALARM
} from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'
import { IUnitControlQueueBasicModel } from '@apps-common/types/portal/unit-control'

import { useMyMonitoringGetUnitControlHistoryByControl } from '@apps-common/apis/my-monitoring/control/get-control-history-by-control'
import { getControlType } from '@apps-common/apps/portal/pages/units/pages/manage/components/controls/control-type'
import { useControlTheme } from '@apps-common/hooks/useControlTheme'
import { ENV_APP_PROJECT } from '@apps-common/lib/environment'
import { HistorySkeletonCard } from './HistorySkeletonCard'
import { HistoryResultMessageDisplay } from './components/HistoryResultMessageDisplay'

const TAKE = 15
const project = ENV_APP_PROJECT

interface IProps {
  controlId: number
  unitId?: string
  requiresControlState?: boolean
}

export const ControlHistoryView: React.FC<IProps> = ({ controlId, unitId, requiresControlState }) => {
  const { resolve } = useControlTheme()

  const useControlHistoryHook = useMemo(() => {
    if (project === 'my-monitoring') {
      return useMyMonitoringGetUnitControlHistoryByControl
    }

    return useGetUnitControlHistoryByControl
  }, [])

  const {
    isLoading: isLoadingById,
    isFetching: isFetchingById,
    isRefetching: isRefetchingById,
    isError: isErrorById,
    data: dataById,
    refetch: refetchById,
    fetchNextPage: fetchNextPageById,
    hasNextPage: hasNextPageById
  } = useControlHistoryHook({ controlId, take: TAKE, enabled: unitId === undefined })

  const data = useMemo(
    () =>
      dataById?.pages.reduce((acc: IUnitControlQueueBasicModel[], curr: IUnitControlQueueBasicModel[]) => [...acc, ...(curr || [])], []),
    [unitId, dataById]
  )

  const getCommandState = (item: IUnitControlQueueBasicModel) => {
    try {
      return item.commands[0].commandState
    } catch {
      // fall through
      // return unknown
    }

    return CONTROL_STATE_UNKNOWN
  }

  const getStateBody = (item: IUnitControlQueueBasicModel) => {
    const state = getCommandState(item)

    if (item?.controlTypeId === CONTROL_TYPE_ALARM || item?.controlTypeId === CONTROL_TYPE_ACCESS_CONTROL) {
      if (state === CONTROL_STATE_ON) {
        return (
          <div className='flex flex-col items-center justify-center gap-[10px]'>
            <commonIcons.armed className='text-[1.25rem]' />
          </div>
        )
      }
      if (state === CONTROL_STATE_OFF) {
        return (
          <div className='flex flex-col items-center justify-center gap-[10px]'>
            <commonIcons.disarmed className='text-[1.25rem]' />
          </div>
        )
      }

      return (
        <div className='flex flex-col items-center justify-center gap-[10px]'>
          <commonIcons.unknown className='text-[1rem]' />
        </div>
      )
    }

    if (requiresControlState) {
      if (state === CONTROL_STATE_ON) {
        return (
          <div className='flex flex-col items-center justify-center gap-[10px]'>
            <div className='uppercase jost-bold text-[0.85rem]'>on</div>
          </div>
        )
      }
      if (state === CONTROL_STATE_OFF) {
        return (
          <div className='flex flex-col items-center justify-center gap-[10px]'>
            <div className='uppercase jost-bold text-[0.85rem]'>off</div>
          </div>
        )
      }

      return (
        <div className='flex flex-col items-center justify-center gap-[10px]'>
          <commonIcons.unknown className='text-[1rem]' />
        </div>
      )
    }

    if (state === CONTROL_STATE_ON) {
      return (
        <div className='flex flex-col items-center justify-center gap-[10px]'>
          <commonIcons.powerAlt className='text-[1.25rem]' />
        </div>
      )
    }
    if (state === CONTROL_STATE_OFF) {
      return (
        <div className='flex flex-col items-center justify-center gap-[10px]'>
          <commonIcons.powerOffAlt className='text-[1.25rem]' />
        </div>
      )
    }

    return (
      <div className='flex flex-col items-center justify-center gap-[10px]'>
        <commonIcons.unknown className='text-[1rem]' />
      </div>
    )
  }

  const loading = isLoadingById || isErrorById

  return (
    <>
      <PullRefresh loading={isRefetchingById} callback={refetchById} />
      <div className='flex flex-col gap-[10px]'>
        {(loading || !data) && Array.from(Array(TAKE).keys()).map((i) => <HistorySkeletonCard key={i.toString()} />)}
        {!loading &&
          data &&
          data.map((d, i) => {
            const state = getCommandState(d)
            const stateBody = getStateBody(d)
            const controlType = getControlType(d.controlTypeId, d.requiresControlState)
            const colors = resolve(controlType, state.toLowerCase())

            return (
              <motion.div
                key={`control-history-card-${d.id}-${i}`}
                {...motionFadeIn(0.3)}
                className='relative overflow-hidden w-full bg-surface text-text border border-border rounded-[24px] pl-[15px] py-[10px] flex items-end justify-between'
              >
                <div className='flex flex-col items-start justify-between'>
                  <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-[5px] w-full border-b border-border pb-1 mb-2'>
                    <div className='text-[0.8em] leading-[1em] text-text-muted/70 pl-1'>
                      {moment(d.dateAdded).format('Do MMM YY, hh:mm a')}
                    </div>
                  </div>
                  <div className='text-[0.85em] flex items-center justify-start gap-[8px]'>
                    {d.result && (
                      <div className='bg-success text-on-success p-[5px] rounded-[12px]'>
                        <commonIcons.checkCircle className='text-[1.2em]' />
                      </div>
                    )}
                    {!d.result && (
                      <div className='bg-warning text-on-warning p-[5px] rounded-[12px]'>
                        <commonIcons.toastWarning className='text-[1.2em]' />
                      </div>
                    )}
                    <div>
                      <div className='text-[0.9em] leading-[1em] text-text p-[2px]'>{d.userName}</div>
                      <div className='text-[0.9em]'>{valueStringCheck(d.msg)}</div>
                    </div>
                  </div>
                </div>
                <div
                  className={classNames(
                    'absolute bottom-0 right-0 m-[7px] uppercase jost-medium text-[0.7em] leading-[1em] p-3 rounded-2xl flex flex-col items-center justify-center gap-[10px]',
                    'opacity-80',
                    colors.bg,
                    colors.text,
                    colors.border
                  )}
                >
                  {stateBody}
                </div>
              </motion.div>
            )
          })}
        <HistoryResultMessageDisplay
          hasNoResults={!loading && !isFetchingById && !hasNextPageById && data && data.length === 0}
          isEndOfResults={!loading && !isFetchingById && !hasNextPageById && data && data.length > 0}
        />
      </div>
      <IonInfiniteScroll
        onIonInfinite={async (ev) => {
          if (!hasNextPageById) {
            ev.target.complete()

            return
          }

          await fetchNextPageById()

          ev.target.complete()
        }}
      >
        <InfiniteLoader loading={!loading && isFetchingById} />
      </IonInfiniteScroll>
    </>
  )
}
